"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.modifyAttribute = exports.newAttribute = exports.AttributeBuilder = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2021 GoodData Corporation
var identity_1 = tslib_1.__importDefault(require("lodash/identity"));
var cloneDeep_1 = tslib_1.__importDefault(require("lodash/cloneDeep"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var index_1 = require("./index");
var objRef_1 = require("../../objRef");
var factory_1 = require("../../objRef/factory");
var spark_md5_1 = tslib_1.__importDefault(require("spark-md5"));
var sanitizeLocalId_1 = require("../../sanitizeLocalId");
/**
 * Builder for attributes.
 *
 * Do not instantiate this class directly. Instead use {@link newAttribute} or {@link modifyAttribute}.
 *
 * @public
 */
var AttributeBuilder = /** @class */ (function () {
    /**
     * @internal
     */
    function AttributeBuilder(input) {
        var _this = this;
        this.customLocalId = false;
        /**
         * Sets alias - alternative title - for the attribute. This value will then be used in various
         * chart-specific descriptive elements. For convenience if no alias is specified, the attribute
         * will fall back to server-defined value.
         *
         * @param alias - alias to use instead of attribute title; undefined to use server-defined value
         */
        this.alias = function (alias) {
            if (!alias) {
                return _this.noAlias();
            }
            _this.attribute.alias = alias;
            return _this;
        };
        /**
         * Resets alias - alternative title - set for the attribute. The server-defined title of the attribute
         * will be used instead.
         */
        this.noAlias = function () {
            delete _this.attribute.alias;
            return _this;
        };
        /**
         * Sets display form reference.
         *
         * @param ref - new ref to use
         */
        this.displayForm = function (ref) {
            _this.attribute.displayForm = ref;
            return _this;
        };
        /**
         * Sets local identifier (localId) for the attribute. LocalId can be used to reference the attribute
         * within the execution definition.
         *
         * Normally, builder will generate localId based on contents of the attribute definition - taking all
         * properties into account: in typical scenarios you don't have to call this function at all. The only exception
         * where you have to provide custom local id is if your execution must contain the exact same attribute twice.
         *
         * For convenience, this method also accepts 'undefined', which indicates that the default local id generation
         * logic should be used.
         *
         * @param localId - local identifier to set; if not specified, the builder will ensure local id will
         * be generated
         */
        this.localId = function (localId) {
            if (!localId || localId.trim().length === 0) {
                return _this.defaultLocalId();
            }
            _this.attribute.localIdentifier = localId;
            _this.customLocalId = true;
            return _this;
        };
        /**
         * Indicates that the attribute's localId should be generated using the default local-id generator logic.
         */
        this.defaultLocalId = function () {
            _this.attribute.localIdentifier = "";
            _this.customLocalId = false;
            return _this;
        };
        /**
         * Creates the IAttribute instance.
         */
        this.build = function () {
            var localIdentifier = _this.getOrGenerateLocalId();
            return {
                attribute: tslib_1.__assign(tslib_1.__assign({}, _this.attribute), { localIdentifier: localIdentifier }),
            };
        };
        if (index_1.isAttribute(input)) {
            this.attribute = cloneDeep_1.default(input.attribute);
            this.customLocalId = true;
        }
        else {
            var displayForm = objRef_1.isObjRef(input) ? input : factory_1.idRef(input, "displayForm");
            this.attribute = {
                displayForm: displayForm,
                localIdentifier: "",
            };
        }
    }
    AttributeBuilder.prototype.getOrGenerateLocalId = function () {
        if (this.customLocalId && !isEmpty_1.default(this.attribute.localIdentifier)) {
            return this.attribute.localIdentifier;
        }
        return sanitizeLocalId_1.sanitizeLocalId(["a", this.calculateAliasHash(), objRef_1.objRefToString(this.attribute.displayForm)]
            .filter(function (part) { return !isEmpty_1.default(part); })
            .join("_"));
    };
    AttributeBuilder.prototype.calculateAliasHash = function () {
        if (!this.attribute.alias) {
            return "";
        }
        var hasher = new spark_md5_1.default();
        hasher.append(this.attribute.alias);
        return hasher.end().substr(0, 8);
    };
    return AttributeBuilder;
}());
exports.AttributeBuilder = AttributeBuilder;
/**
 * Creates a new attribute with the specified display form ref and optional modifications and localIdentifier.
 * @param displayFormRefOrId - ref or identifier of the attribute display form
 * @param modifications - optional modifications (e.g. alias, etc.)
 * @public
 */
function newAttribute(displayFormRefOrId, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var builder = new AttributeBuilder(displayFormRefOrId);
    return modifications(builder).build();
}
exports.newAttribute = newAttribute;
/**
 * Allows modification of an existing attribute instance.
 *
 * The returned attribute will have the same localId as the original attribute. If you would like to assign
 * new/different local identifier to the attribute, you can do that using the modifications where you can provide
 * either new custom localId or indicate that the attribute should fall back to the auto-generated localId.
 *
 * @param attribute - attribute to modify
 * @param modifications - modification function
 * @public
 */
function modifyAttribute(attribute, modifications) {
    if (modifications === void 0) { modifications = identity_1.default; }
    var builder = new AttributeBuilder(attribute);
    return modifications(builder).build();
}
exports.modifyAttribute = modifyAttribute;
//# sourceMappingURL=factory.js.map